import { useCallback, useMemo } from 'react';
import type { AlignedBox, Transform } from './VisibilityBox';
import { matrixFromCuboid } from './Util';
import { useSharedGeometries } from './useSharedGeometries';
import * as THREE from 'three';

const CHECK_EPSILON = 0.000001;
const CUBOID_MIN = -0.5;
const CUBOID_MAX = 0.5;

const clampLineComponent = (
  coord: number,
  edge: number,
  point0: THREE.Vector3,
  point1: THREE.Vector3
) => {
  const value0 = point0.getComponent(coord);
  const value1 = point1.getComponent(coord);
  const sign = edge > 0 ? 1 : -1;
  if (value1 * sign > edge * sign) {
    // v0 ----- edge ----- v1     <- input
    // v0 ----- v1                <- result
    const alpha = (edge - value0) / (value1 - value0);
    [0, 1, 2].forEach((coord2) => {
      const value20 = point0.getComponent(coord2);
      const value21 = point1.getComponent(coord2);
      const edgeValue = value20 + (value21 - value20) * alpha;
      point1.setComponent(coord2, edgeValue);
    });
  }
};
const clampLineByUnitCuboid = (line: THREE.Line3) =>
  // clamp line by unit cuboid edges
  [0, 1, 2].forEach((coord) => {
    const v0 = line.start.getComponent(coord);
    const v1 = line.end.getComponent(coord);
    const [startp, endp, start, end] =
      v0 < v1 ? [v0, v1, line.start, line.end] : [v1, v0, line.end, line.start];
    if (startp < CUBOID_MAX && endp > CUBOID_MAX) {
      clampLineComponent(coord, CUBOID_MAX, start, end);
    }
    if (startp < CUBOID_MIN && endp > CUBOID_MIN) {
      clampLineComponent(coord, CUBOID_MIN, end, start);
    }
  });

const shrinkUnitCuboidByLines = (
  currentCuboid: AlignedBox,
  shrinkCoord: number,
  shrinkSign: number,
  lines: THREE.Line3[]
): boolean => {
  let hasChanges = false;
  if (shrinkSign > 0) {
    const prevValue = currentCuboid.min[shrinkCoord];
    const newValue = lines.reduce(
      (accumulator, line) =>
        Math.max(
          accumulator,
          line.start.getComponent(shrinkCoord),
          line.end.getComponent(shrinkCoord)
        ),
      prevValue
    );
    if (Math.abs(newValue - prevValue) >= CHECK_EPSILON) {
      currentCuboid.min[shrinkCoord] = newValue;
      hasChanges = true;
    }
  } else {
    const prevValue = currentCuboid.max[shrinkCoord];
    const newValue = lines.reduce(
      (accumulator, line) =>
        Math.min(
          accumulator,
          line.start.getComponent(shrinkCoord),
          line.end.getComponent(shrinkCoord)
        ),
      prevValue
    );
    if (Math.abs(newValue - prevValue) >= CHECK_EPSILON) {
      currentCuboid.max[shrinkCoord] = newValue;
      hasChanges = true;
    }
  }
  return hasChanges;
};

export const useShrinkCuboid = () => {
  const { cuboidLines } = useSharedGeometries();

  const [points, lineIndexes] = useMemo(() => {
    // 8 points, 12 lines
    const lineIndexes = cuboidLines.getIndex();
    const positions = cuboidLines.getAttribute('position');
    const points = Array(8)
      .fill(0)
      .map(
        (_, index) =>
          new THREE.Vector3(
            positions.getX(index), // x
            positions.getY(index), // y
            positions.getZ(index)
          )
      );
    return [points, lineIndexes];
  }, [cuboidLines]);

  const makeClampedCuboidLines = useCallback(
    (cuboidMatrix: THREE.Matrix4, localMatrixInverse: THREE.Matrix4): THREE.Line3[] => {
      if (!lineIndexes) return [];
      const localPoints = points.map((point) =>
        point.clone().applyMatrix4(cuboidMatrix).applyMatrix4(localMatrixInverse)
      );
      return Array(12)
        .fill(0)
        .map((_, index) => {
          const p0 = lineIndexes?.getComponent(index, 0);
          const p1 = lineIndexes?.getComponent(index, 1);
          const line = new THREE.Line3(localPoints[p0].clone(), localPoints[p1].clone());
          const hasIntersection = ![0, 1, 2].some((coord) => {
            const start = line.start.getComponent(coord);
            const end = line.end.getComponent(coord);
            const isOutside =
              (start < CUBOID_MIN && end < CUBOID_MIN) || (start > CUBOID_MAX && end > CUBOID_MAX);
            return isOutside;
          });
          if (!hasIntersection) {
            return undefined;
          }
          clampLineByUnitCuboid(line);
          return line;
        })
        .filter((value) => !!value);
    },
    [lineIndexes, points]
  );

  const shrinkCuboid = useCallback(
    (
      cuboid: Transform,
      cuboids: Transform[]
    ): { shrinkedCuboid?: Transform; hasError?: boolean } => {
      const currentCuboid: AlignedBox = {
        min: [CUBOID_MIN, CUBOID_MIN, CUBOID_MIN],
        max: [CUBOID_MAX, CUBOID_MAX, CUBOID_MAX],
      };
      let hasChanges = false;

      const matrix = matrixFromCuboid(cuboid);
      const matrixInverse = matrix.clone().invert();

      cuboids.forEach((cuboid0) => {
        // center of cuboid0 in local space of cuboid
        const center0 = new THREE.Vector3(...cuboid0.position)
          .applyMatrix4(matrixInverse)
          .toArray();
        if (center0.every((value) => Math.abs(value) < CHECK_EPSILON)) {
          // it's a same cuboid or cuboid centers too close to shrink
          return;
        }
        const matrix0 = matrixFromCuboid(cuboid0);
        const matrixInverse0 = matrix0.clone().invert();

        // lines of cuboid0 in cuboid local space clamped by unit cuboid
        const linesA = makeClampedCuboidLines(matrix0, matrixInverse);

        // lines of cuboid in cuboid0 local space clamped by unit cuboid
        // and transformed back to cuboid local space
        const linesB = makeClampedCuboidLines(matrix, matrixInverse0).map((line) =>
          line.applyMatrix4(matrix0).applyMatrix4(matrixInverse)
        );

        // choose coordinate with the maximal absolute value as a shrinking coordinate
        const shrinkCoord = center0.reduce(
          (maxIndex, currentValue, index) =>
            Math.abs(currentValue) > Math.abs(center0[maxIndex]) ? index : maxIndex,
          0
        );
        const shrinkSign = center0[shrinkCoord] < 0 ? 1 : -1;
        // console.log('shrinkCoord', { shrinkCoord, shrinkSign });

        // shrink cuboid in it's local space to exclude lines
        if (shrinkUnitCuboidByLines(currentCuboid, shrinkCoord, shrinkSign, linesA)) {
          hasChanges = true;
        }
        if (shrinkUnitCuboidByLines(currentCuboid, shrinkCoord, shrinkSign, linesB)) {
          hasChanges = true;
        }
      });
      if (!hasChanges) {
        // console.log('No Shrink');
        return {};
      }
      if (currentCuboid.min.some((value, index) => value >= currentCuboid.max[index])) {
        // console.log('Invalid cuboid after shrink');
        return { hasError: true };
      }
      const oldScale = new THREE.Vector3(...cuboid.scale);

      const newMin = new THREE.Vector3(...currentCuboid.min);
      const newMax = new THREE.Vector3(...currentCuboid.max);
      const newPosition = newMin.clone().add(newMax).multiplyScalar(0.5).applyMatrix4(matrix);
      const newScale = newMax.clone().sub(newMin).multiply(oldScale);

      const shrinkedCuboid: Transform = {
        position: [newPosition.x, newPosition.y, newPosition.z],
        scale: [newScale.x, newScale.y, newScale.z],
        rotation: cuboid.rotation,
      };
      return { shrinkedCuboid };
    },
    [makeClampedCuboidLines]
  );

  return shrinkCuboid;
};
